import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableHead,
  TableRow, Chip, CircularProgress, Box, TextField, InputAdornment,
  Select, MenuItem, IconButton, Tooltip, Dialog, DialogTitle,
  DialogContent, DialogActions, Button, Alert,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_API_URL || 'https://watchpassion.vercel.app/api';

export default function AdminUsersPage() {
  const { authHeader, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/users`, { headers: authHeader() });
      setUsers(data.users || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const changeRole = async (id, role) => {
    await axios.patch(`${API}/admin/users/${id}`, { role }, { headers: authHeader() });
    setUsers(prev => prev.map(u => u.id === id ? { ...u, role } : u));
  };

  const deleteUser = async () => {
    if (!deleteDialog) return;
    setDeleting(true);
    await axios.delete(`${API}/admin/users/${deleteDialog.id}`, { headers: authHeader() });
    setUsers(prev => prev.filter(u => u.id !== deleteDialog.id));
    setDeleting(false);
    setDeleteDialog(null);
  };

  const filtered = users.filter(u =>
    !search || u.email?.toLowerCase().includes(search.toLowerCase()) ||
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) return <Container sx={{ pt: 6, textAlign: 'center' }}><CircularProgress /></Container>;

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Typography variant="h4" fontWeight="bold">Utenti</Typography>
        <Chip label={`${users.length} registrati`} variant="outlined" />
      </Box>

      <TextField size="small" placeholder="Cerca per nome o email..." value={search}
        onChange={e => setSearch(e.target.value)} sx={{ mb: 3, width: 320 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }} />

      <Paper>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.50' }}>
              <TableCell>Nome</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Registrato il</TableCell>
              <TableCell>Ruolo</TableCell>
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}>Nessun utente</TableCell></TableRow>
            )}
            {filtered.map(u => {
              const isSelf = currentUser?.email === u.email;
              return (
                <TableRow key={u.id} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {u.role === 'admin'
                        ? <AdminPanelSettingsIcon fontSize="small" color="primary" />
                        : <PersonIcon fontSize="small" color="disabled" />}
                      <Typography variant="body2">{u.name || '—'}</Typography>
                      {isSelf && <Chip label="tu" size="small" color="primary" variant="outlined" />}
                    </Box>
                  </TableCell>
                  <TableCell><Typography variant="body2">{u.email}</Typography></TableCell>
                  <TableCell><Typography variant="body2">{new Date(u.created_at).toLocaleDateString('it-IT')}</Typography></TableCell>
                  <TableCell>
                    <Select size="small" value={u.role || 'user'} disabled={isSelf}
                      onChange={e => changeRole(u.id, e.target.value)}
                      renderValue={v => <Chip label={v} color={v === 'admin' ? 'primary' : 'default'} size="small" />}>
                      <MenuItem value="user">user</MenuItem>
                      <MenuItem value="admin">admin</MenuItem>
                    </Select>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={isSelf ? 'Non puoi eliminare te stesso' : 'Elimina utente'}>
                      <span>
                        <IconButton size="small" color="error" disabled={isSelf}
                          onClick={() => setDeleteDialog(u)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Dialog open={!!deleteDialog} onClose={() => setDeleteDialog(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Elimina utente</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>Questa azione è irreversibile.</Alert>
          <Typography>Vuoi eliminare l'utente <strong>{deleteDialog?.email}</strong>?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(null)}>Annulla</Button>
          <Button variant="contained" color="error" onClick={deleteUser} disabled={deleting}>
            {deleting ? <CircularProgress size={16} /> : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
